import React, { useEffect } from "react";

const SERVICE_PROVIDERS = [
  {
    service: "Payment processing",
    provider:
      "PayPal (including Venmo), Apple Pay, Google Pay, and Braintree (credit card processing)",
    information:
      "Name, billing address, order amount, payment details you enter with them",
    purpose:
      "Processing payments, refunds, saved payment methods, fraud prevention",
  },
  {
    service: "Shipping and delivery",
    provider: "USPS, UPS, FedEx",
    information: "Name, shipping address, phone, email, package details",
    purpose: "Delivering orders and sending tracking updates",
  },
  {
    service: "Customs (international orders)",
    provider: "Carriers and customs brokers",
    information: "Name, address, phone, contents and value of shipment",
    purpose: "Clearing customs and calculating duties",
  },
  {
    service: "Drop-ship suppliers",
    provider:
      "Manufacturers who ship certain products directly to you, some located outside the U.S.",
    information: "Name, shipping address, phone, items ordered",
    purpose: "Delivering products shipped directly by the manufacturer",
  },
  {
    service: "Email marketing and automation",
    provider: "Mailchimp (Intuit Inc.) and Google",
    information:
      "Name, email, order and product data, cart and browsing activity, email engagement",
    purpose:
      "Newsletters, order-related and automated emails, audience segmentation",
  },
  {
    service: "Accounting",
    provider: "QuickBooks Online (Intuit Inc.)",
    information: "Name, business name, addresses, order and payment records",
    purpose: "Bookkeeping and tax records",
  },
  {
    service: "Website hosting and e-commerce",
    provider: "Amazon Web Services (AWS)",
    information: "All information processed by the website",
    purpose: "Operating tubedepot.com",
  },
  {
    service: "Analytics",
    provider: "Google Analytics",
    information: "Device, usage, and browsing data",
    purpose: "Understanding site traffic and performance",
  },
];

const PrivacyandSecurity = () => {
  useEffect(() => {
    document.title = "Privacy Policy | TubeDepot.com";
    gtag("event", "page_view", {
      page_path: window.location.pathname,
    });
  }, []);

  return (
    <div className="container">
      <div className="content-margin bare-table constrict privacy-policy">
        <h2 className="title">TubeDepot Privacy Policy</h2>
        <p className="effective-date">
          <strong>Effective date:</strong> October 7, 2026
        </p>

        <h3>About This Policy</h3>
        <p>
          This Privacy Policy explains how TubeDepot, LLC ("TubeDepot," "we,"
          "us") collects, uses, and shares personal information when you visit
          tubedepot.com, place an order, contact us, or receive our emails. It
          applies only to TubeDepot's website and services, not to third-party
          sites you may reach from it.
        </p>
        <p>
          We do not sell your personal information. We share it with companies
          that help us run our business, such as payment processors, shipping
          carriers, and our email marketing provider, as described below.
        </p>

        <h3>Information We Collect</h3>
        <p>
          You can browse product and company information without telling us who
          you are. We collect personal information when you give it to us,
          automatically as you use the site, and occasionally from our service
          providers.
        </p>
        <h4>Information you give us</h4>
        <ul>
          <li>
            <strong>Contact and account details:</strong> name, business name,
            billing and shipping addresses, phone number, email address, and
            account password.
          </li>
          <li>
            <strong>Order details:</strong> products purchased, order amounts,
            shipping method, and order history.
          </li>
          <li>
            <strong>Communications:</strong> messages you send us by email, web
            form, or phone, including repair or product questions.
          </li>
          <li>
            <strong>Payment information:</strong> you enter payment details
            directly with our payment processor. We do not receive or store full
            card numbers on our servers.
          </li>
        </ul>
        <h4>Information collected automatically</h4>
        <ul>
          <li>
            <strong>Device and usage data:</strong> IP address, browser type,
            device type, referring website, pages viewed, products viewed, items
            added to your cart, and time spent on pages.
          </li>
          <li>
            <strong>Email engagement data:</strong> whether you open our emails
            and which links you click.
          </li>
          <li>
            When we know who you are (for example, because you are logged in,
            have placed an order, or clicked a link in one of our emails), we
            may connect this browsing and email activity to your email address
            or customer account.
          </li>
        </ul>
        <h4>Information from others</h4>
        <ul>
          <li>
            Shipping carriers provide tracking and delivery status for your
            orders.
          </li>
          <li>
            Our payment processor confirms whether a payment succeeded and may
            share limited fraud-prevention information.
          </li>
        </ul>

        <h3>How We Use Your Information</h3>
        <ul>
          <li>
            <strong>Fulfilling orders:</strong> processing payments, shipping
            products, sending order confirmations and tracking updates, and
            handling returns and warranty claims.
          </li>
          <li>
            <strong>Customer service:</strong> answering questions and providing
            technical or product support.
          </li>
          <li>
            <strong>Marketing emails:</strong> sending newsletters, new product
            announcements, and promotions to customers and subscribers. You can
            unsubscribe at any time.
          </li>
          <li>
            <strong>Personalized and automated emails:</strong> using your order
            history, browsing activity, and cart activity to send relevant
            messages, such as a reminder about items left in your cart, products
            you viewed, restock notices, or recommendations based on past
            purchases.
          </li>
          <li>
            <strong>Advertising:</strong> showing TubeDepot ads to customers and
            similar audiences on platforms such as Facebook, Instagram, and
            Google, as described under "Advertising" below.
          </li>
          <li>
            <strong>Improving the site:</strong> understanding how visitors use
            tubedepot.com so we can improve products, content, and navigation.
          </li>
          <li>
            <strong>Security and legal compliance:</strong> preventing fraud,
            protecting our systems, keeping accounting and tax records, and
            complying with legal obligations.
          </li>
        </ul>

        <h3>How We Share Your Information</h3>
        <p>
          We do not sell, rent, or trade your personal information. We share it
          only in the situations below.
        </p>
        <p>
          <strong>Service providers.</strong> We share information with
          companies that perform services for us. They may use it only to
          provide those services to TubeDepot and must protect it.
        </p>
        <table>
          <thead>
            <tr>
              <th scope="col">Service</th>
              <th scope="col">Provider</th>
              <th scope="col">Information shared</th>
              <th scope="col">Purpose</th>
            </tr>
          </thead>
          <tbody>
            {SERVICE_PROVIDERS.map(
              ({ service, provider, information, purpose }) => (
                <tr key={service}>
                  <th scope="row">{service}</th>
                  <td data-label="Provider">{provider}</td>
                  <td data-label="Information shared">{information}</td>
                  <td data-label="Purpose">{purpose}</td>
                </tr>
              ),
            )}
          </tbody>
        </table>
        <p>
          <strong>Advertising.</strong> We may share your email address in a
          protected (hashed) form, and allow advertising partners such as Meta
          and Google to collect browsing data through cookies on our site, so we
          can show TubeDepot ads to you or to people with similar interests.
          Some state laws consider this "sharing" for targeted advertising. You
          can opt out as described under "Your Choices and Rights."
        </p>
        <p>
          <strong>Legal and safety.</strong> We may disclose information when
          required by law, subpoena, or government request, or when needed to
          protect TubeDepot, our customers, or others from fraud or harm.
        </p>
        <p>
          <strong>Business transfers.</strong> If TubeDepot is involved in a
          merger, acquisition, or sale of assets, customer information may be
          transferred as part of that transaction, subject to this policy.
        </p>
        <p>
          <strong>Aggregate data.</strong> We may share statistics about sales
          and site traffic that do not identify any individual.
        </p>

        <h3>Cookies and Tracking Technologies</h3>
        <p>
          We and our service providers use cookies, pixels, and similar
          technologies on tubedepot.com and in our emails.
        </p>
        <ul>
          <li>
            <strong>Essential cookies</strong> keep you logged in, remember your
            cart, and keep checkout secure. The site will not work properly
            without them.
          </li>
          <li>
            <strong>Analytics cookies</strong> help us understand how visitors
            use the site.
          </li>
          <li>
            <strong>Marketing cookies and tracking code</strong>, including
            Mailchimp's site tracking, record products you view and items in
            your cart so we can send relevant follow-up emails. We may also use
            advertising pixels from partners such as Meta and Google to show you
            TubeDepot ads on other sites.
          </li>
          <li>
            <strong>Email tracking:</strong> our emails contain small images and
            tracked links that tell us whether an email was opened and which
            links were clicked.
          </li>
        </ul>
        <p>
          You can block or delete cookies in your browser settings, though some
          parts of the site may stop working. You can also turn off automatic
          image loading in your email program to limit open tracking.
        </p>

        <h3>Your Choices and Rights</h3>
        <ul>
          <li>
            <strong>Marketing emails:</strong> click "unsubscribe" in any
            marketing email, or email{" "}
            <a href="mailto:info@tubedepot.com">info@tubedepot.com</a>. You will
            still receive order confirmations, shipping notices, and other
            messages about your purchases.
          </li>
          <li>
            <strong>Targeted advertising:</strong> email{" "}
            <a href="mailto:info@tubedepot.com?subject=Opt%20out%20of%20targeted%20advertising">
              info@tubedepot.com
            </a>{" "}
            with the subject "Opt out of targeted advertising," and we will stop
            using your information for targeted ads. You can also adjust ad
            preferences directly with Meta and Google.
          </li>
          <li>
            <strong>Access, correction, and deletion:</strong> email{" "}
            <a href="mailto:info@tubedepot.com">info@tubedepot.com</a> to
            request a copy of the personal information we hold about you,
            correct it, or delete it. To protect your account, we may need to
            verify your identity, for example by responding from the email
            address on your account. Deleting your information also closes your
            account. We may keep records we are legally required to keep, such
            as tax and transaction records.
          </li>
          <li>
            <strong>Cookies:</strong> manage cookies through your browser
            settings.
          </li>
        </ul>
        <p>
          Depending on where you live, state or national privacy laws may give
          you additional rights, including the right to appeal a decision we
          make about your request. To appeal, reply to our decision or email{" "}
          <a href="mailto:info@tubedepot.com?subject=Privacy%20appeal">
            info@tubedepot.com
          </a>{" "}
          with the subject "Privacy appeal." We will not treat you differently
          for exercising your privacy rights.
        </p>

        <h3>Data Retention</h3>
        <p>
          We keep account and order information while your account is active and
          as long as needed for warranty, returns, accounting, and tax purposes,
          generally 7 years after your last order. Marketing data is kept until
          you unsubscribe or ask us to delete it. Browsing activity used for
          automated emails is kept for up to 12 months.
        </p>

        <h3>How We Protect Your Information</h3>
        <p>
          We use reasonable technical and organizational safeguards, including
          encrypted (SSL/TLS) connections, restricted access to customer data,
          and employee training. Payment details are handled by our payment
          processor, not stored on our servers. No system is completely secure,
          so please use a strong, unique password and contact us promptly if you
          believe your account has been compromised.
        </p>

        <h3>Children's Privacy</h3>
        <p>
          TubeDepot.com is not directed to children under 13, and we do not
          knowingly collect personal information from them. If you believe a
          child has given us personal information, contact us and we will delete
          it.
        </p>

        <h3>International Customers</h3>
        <p>
          TubeDepot is based in the United States, and we process and store
          information in the United States. If you order from outside the U.S.,
          your information will be transferred to and processed in the U.S.,
          where privacy laws may differ from those in your country. Some of our
          suppliers, carriers, and service providers are located outside the
          U.S., so information needed to fulfill your order may also be
          processed in other countries.
        </p>

        <h3>Third-Party Websites</h3>
        <p>
          Our site links to manufacturer pages, datasheets, and other websites
          we do not control. Those sites have their own privacy policies, and we
          are not responsible for how they handle your information.
        </p>

        <h3>Changes to This Policy</h3>
        <p>
          We may update this policy as our business or the law changes. We will
          post the updated version here with a new effective date. If we make a
          significant change to how we use or share your personal information,
          we will notify you by email or with a notice on our website before the
          change takes effect.
        </p>

        <h3>Contact Us</h3>
        <p>Questions or requests about this policy or your information:</p>
        <address>
          TubeDepot, LLC
          <br />
          1958 Vanderhorn Drive, Memphis, TN 38134
          <br />
          Email: <a href="mailto:info@tubedepot.com">info@tubedepot.com</a>
          <br />
          Phone: <a href="tel:+18772897994">877-289-7994</a>
        </address>
        <br />
        <br />
      </div>
    </div>
  );
};

export default PrivacyandSecurity;
